const TM_AUTH_CONTEXT_KEY = 'guestwifi_tm_auth_context'
const CAPTIVE_PORTAL_CONTEXT_KEY = 'guestwifi_captive_portal_context'
const GUEST_FLOW_SESSION_KEY = 'guestwifi_guest_flow_session'

const TM_CONTEXT_TTL_MS = 15 * 60 * 1000 // 15 minutes, aligned with backend TM flow timeout
const GUEST_SESSION_TTL_MS = 5 * 60 * 1000

const ALLOWED_REDIRECT_DOMAINS = ['target.com', 'network-auth.com']

/**
 * Validates that a URL is safe to use as a redirect target.
 * Must be https:// and belong to an allowed domain.
 * Used before every window.location.replace() to prevent open-redirect attacks
 * via tampered client-side storage values.
 */
export function isSafeRedirectUrl(url) {
  if (!url) return false
  try {
    const parsed = new URL(url)
    return (
      parsed.protocol === 'https:' &&
      ALLOWED_REDIRECT_DOMAINS.some(
        (domain) =>
          parsed.hostname === domain || parsed.hostname.endsWith(`.${domain}`),
      )
    )
  } catch {
    return false
  }
}

function safeJsonParse(value) {
  if (!value) return null

  try {
    return JSON.parse(value)
  } catch (error) {
    console.error('Failed to parse stored context', error)
    return null
  }
}

function writeToStorage(key, payload) {
  const serialized = JSON.stringify(payload)

  try {
    sessionStorage.setItem(key, serialized)
  } catch (error) {
    console.error(`Failed to write ${key} to sessionStorage`, error)
  }
}

function readFromStorage(key) {
  const sessionValue = safeJsonParse(sessionStorage.getItem(key))
  if (sessionValue) return sessionValue

  return null
}

function clearFromStorage(key) {
  try {
    sessionStorage.removeItem(key)
  } catch (error) {
    console.error(`Failed to clear ${key} from sessionStorage`, error)
  }
}

function isExpired(context, ttlMs) {
  if (!context?.savedAt) return false
  return Date.now() - context.savedAt > ttlMs
}

export function saveTMAuthContext(context) {
  try {
    writeToStorage(TM_AUTH_CONTEXT_KEY, {
      ...context,
      savedAt: context?.savedAt || Date.now(),
      step: context?.step || 'start',
    })
  } catch (error) {
    console.error('Failed to persist TM auth context', error)
  }
}

export function loadTMAuthContext() {
  try {
    const value = readFromStorage(TM_AUTH_CONTEXT_KEY)
    if (!value) return null

    if (isExpired(value, TM_CONTEXT_TTL_MS)) {
      clearTMAuthContext()
      return null
    }

    return value
  } catch (error) {
    console.error('Failed to load TM auth context', error)
    return null
  }
}

export function clearTMAuthContext() {
  clearFromStorage(TM_AUTH_CONTEXT_KEY)
}

export function saveCaptivePortalContext(context) {
  try {
    writeToStorage(CAPTIVE_PORTAL_CONTEXT_KEY, {
      ...context,
      savedAt: context?.savedAt || Date.now(),
    })
  } catch (error) {
    console.error('Failed to persist captive portal context', error)
  }
}

export function loadCaptivePortalContext() {
  try {
    const value = readFromStorage(CAPTIVE_PORTAL_CONTEXT_KEY)
    if (!value) return null

    if (isExpired(value, TM_CONTEXT_TTL_MS)) {
      clearCaptivePortalContext()
      return null
    }

    return value
  } catch (error) {
    console.error('Failed to load captive portal context', error)
    return null
  }
}

export function clearCaptivePortalContext() {
  clearFromStorage(CAPTIVE_PORTAL_CONTEXT_KEY)
}

export function saveGuestFlowSession(session) {
  try {
    writeToStorage(GUEST_FLOW_SESSION_KEY, {
      ...session,
      savedAt: session?.savedAt || Date.now(),
    })
  } catch (error) {
    console.error('Failed to persist guest flow session', error)
  }
}

export function loadGuestFlowSession(expectedContext = {}) {
  try {
    const value = readFromStorage(GUEST_FLOW_SESSION_KEY)
    if (!value) return null

    const expiresAt = value?.expiresAt ? Date.parse(value.expiresAt) : null
    if (
      (expiresAt && Date.now() >= expiresAt) ||
      isExpired(value, GUEST_SESSION_TTL_MS)
    ) {
      clearGuestFlowSession()
      return null
    }

    if (
      (expectedContext.mac && value.mac !== expectedContext.mac) ||
      (expectedContext.loc && value.loc !== expectedContext.loc) ||
      (expectedContext.successUrl &&
        value.successUrl !== expectedContext.successUrl)
    ) {
      clearGuestFlowSession()
      return null
    }

    return value
  } catch (error) {
    console.error('Failed to load guest flow session', error)
    return null
  }
}

export function clearGuestFlowSession() {
  clearFromStorage(GUEST_FLOW_SESSION_KEY)
}

export function clearAllGuestWifiState() {
  clearTMAuthContext()
  clearCaptivePortalContext()
  clearGuestFlowSession()
}

export function buildSuccessUrl(baseGrantUrl, userContinueUrl) {
  if (!baseGrantUrl || !userContinueUrl) return null

  try {
    const url = new URL(baseGrantUrl)
    url.searchParams.set('continue_url', userContinueUrl)
    return url.toString()
  } catch (error) {
    console.error('Failed to build successUrl', error)
    return null
  }
}
